import { isAxiosError } from "axios"

import type { PluginLimits, PluginVersion } from "@/api/plugins-api"
import type { TFunction } from "i18next"

export const DEFAULT_PLUGIN_LIMITS: PluginLimits = {
  timeout_seconds: 20,
  response_bytes: 1048576,
  pages: 5,
  calls: 10,
  concurrency: 4,
  per_minute: 60,
  model_bytes: 32768,
}

export function nativeVersionActions(version?: PluginVersion): string {
  const properties = version?.operations[0]?.parameters.properties as
    | Record<string, unknown>
    | undefined
  const action = properties?.action as { enum?: unknown } | undefined
  return Array.isArray(action?.enum)
    ? action.enum.filter((value): value is string => typeof value === "string").join(", ")
    : ""
}

export function pluginErrorCode(error: unknown): string {
  if (isAxiosError(error) && typeof error.response?.data?.error === "string") {
    return error.response.data.error
  }
  return "plugin_service_unavailable"
}

/**
 * Render a plugin error code as a sentence. Codes without a translation still
 * reach the reader as the generic sentence plus the raw code, so an unexpected
 * backend code stays diagnosable instead of collapsing into "try again".
 * `alwaysAppendCode` keeps the code visible even for translated sentences, for
 * surfaces where support needs to name the failure (schedule toasts).
 */
export function pluginErrorMessage(
  t: TFunction,
  code?: string,
  options?: { fallback?: string; alwaysAppendCode?: boolean }
): string {
  const generic = options?.fallback ?? t("plugins.error")
  if (!code) return generic
  const translated = t(`plugins.errors.${code}`, { defaultValue: "" })
  if (translated && !options?.alwaysAppendCode) return translated
  return `${translated || generic} (${code})`
}

// Mirror of the backend contract in services/plugins/models.py
// (FORBIDDEN_STATIC_HEADERS, MAX_STATIC_HEADERS, MAX_STATIC_HEADER_VALUE and
// checked_static_headers). The backend is the authority; keep these in step so
// the inline gate and the server give the same verdict.
export const PLUGIN_HEADER_MAX = 8
export const PLUGIN_HEADER_VALUE_MAX = 512
const PLUGIN_HEADER_NAME = /^[A-Za-z0-9!#$%&'*+.^_`|~-]+$/
const RESERVED_PLUGIN_HEADERS = [
  "host",
  "authorization",
  "proxy-authorization",
  "cookie",
  "set-cookie",
  "content-length",
  "content-type",
  "transfer-encoding",
  "connection",
  "upgrade",
  "te",
  "trailer",
  "forwarded",
  "x-forwarded-host",
  "x-forwarded-for",
  "keep-alive",
  "expect",
]

/** Printable ASCII only (0x20-0x7E), exactly what the backend accepts. */
function isPrintableHeaderValue(value: string): boolean {
  for (const character of value) {
    const code = character.codePointAt(0) ?? 0
    if (code < 0x20 || code > 0x7e) return false
  }
  return true
}

/**
 * Parse the admin "Name: value" textarea into the `headers` payload. Invalid is
 * reported rather than silently dropped so the dialog can block publishing with
 * the same `invalid_plugin_header` message the backend would return.
 */
export function parsePluginHeaders(
  text: string,
  authHeaderName?: string
): { headers: Record<string, string>; invalid: boolean } {
  const reserved = new Set(RESERVED_PLUGIN_HEADERS)
  if (authHeaderName?.trim()) reserved.add(authHeaderName.trim().toLowerCase())
  const headers: Record<string, string> = {}
  const seen = new Set<string>()
  let invalid = false
  for (const line of text.split(/\r?\n/)) {
    const trimmed = line.trim()
    if (!trimmed) continue
    const separator = trimmed.indexOf(":")
    const name = separator > 0 ? trimmed.slice(0, separator).trim() : ""
    const value = separator > 0 ? trimmed.slice(separator + 1).trim() : ""
    const key = name.toLowerCase()
    if (
      !PLUGIN_HEADER_NAME.test(name) ||
      reserved.has(key) ||
      seen.has(key) ||
      !value ||
      value.length > PLUGIN_HEADER_VALUE_MAX ||
      !isPrintableHeaderValue(value)
    ) {
      invalid = true
      continue
    }
    seen.add(key)
    headers[name] = value
  }
  if (Object.keys(headers).length > PLUGIN_HEADER_MAX) invalid = true
  return { headers, invalid }
}

export function formatPluginHeaders(headers?: Record<string, string>): string {
  return Object.entries(headers ?? {})
    .map(([name, value]) => `${name}: ${value}`)
    .join("\n")
}

export function startAuthorization(url: string): void {
  const parsed = new URL(url)
  if (parsed.protocol !== "https:" || parsed.username || parsed.password) {
    throw new Error("Invalid authorization URL")
  }
  window.location.assign(parsed.href)
}

export function splitIdentifiers(text: string): string[] {
  return [
    ...new Set(
      text
        .split(/[\s,;]+/)
        .map((item) => item.trim())
        .filter(Boolean)
    ),
  ]
}
